/// <reference path="../fontsource.d.ts" />
import { tableAnatomy as parts } from '@chakra-ui/anatomy';
import { theme as proTheme } from '@chakra-ui/pro-theme';
import { theme as baseTheme, extendTheme, type StyleFunctionProps } from '@chakra-ui/react';
import { createMultiStyleConfigHelpers } from '@chakra-ui/styled-system';
import '@fontsource-variable/inter';

export type ChakraTheme = {
  components: {
    Dashboard: {
      sizes: {
        mainContentTopOffset: string;
        sidePanelTopOffset: string;
      };
    };
  };
};

const helpers = createMultiStyleConfigHelpers(parts.keys);

const baseStyle = helpers.definePartsStyle({
  tr: {
    th: {
      bg: 'transparent',
      fontSize: 'sm',
      borderTop: 'none',
      borderColor: 'border.default',
      color: 'fg.muted',
      fontWeight: 'medium',
      lineHeight: '5',
    },
    td: {
      borderColor: 'border.default',
    },
  },
});

const tableTheme = helpers.defineMultiStyleConfig({
  baseStyle,
});

export const colors = {
  edgeButtonIcon: '#A0AEC0',
  quizBg: '#F4F8FA',
};

const Menu = {
  baseStyle: {
    menu: {
      maxH: '300px',
      py: '4',
      borderRadius: 'xl',
      border: 'none',
      bg: 'teal.500',
    },
  },
};

const Button = {
  variants: {
    custom: {
      bg: 'gray.200',
      color: 'purple.600',
      _hover: {
        bg: 'gray.200',
      },
    },
  },
};

const Modal = {
  sizes: {
    '2xl': {
      dialog: {
        width: '800px',
        height: 'auto',
      },
    },
  },
};

export const brandColors = {
  ...baseTheme.colors,
  brand: {
    ...baseTheme.colors.purple,

    900: '#0f0a2f',
    800: '#1e135d',
    700: '#2d1d8c',
    600: '#4532AD',
    500: '#563FD8',
    400: '#7967e0',
    300: '#9a8de8',
    200: '#bcb3ef',
    100: '#ddd9f7',
  },
};

const dukeLightColors = {
  danger: baseTheme.colors.red[500],
  'danger.emphasis': baseTheme.colors.red[600],
  'danger.deep': baseTheme.colors.red[800],
  'danger.subtle': baseTheme.colors.red[200],
  'danger.muted': baseTheme.colors.red[50],
  warning: baseTheme.colors.orange[400],
  'warning.emphasis': baseTheme.colors.orange[500],
  'warning.deep': baseTheme.colors.orange[700],
  info: baseTheme.colors.blue[500],
  'info.emphasis': baseTheme.colors.blue[600],
  'info.deep': baseTheme.colors.blue[700],
  success: baseTheme.colors.green[500],
  'success.emphasis': baseTheme.colors.green[600],
  'success.deep': baseTheme.colors.green[800],
  'success.subtle': baseTheme.colors.green[200],
  accent: brandColors.brand[500],
  border: {
    default: baseTheme.colors.gray[200],
    emphasis: baseTheme.colors.gray[300],
    active: baseTheme.colors.gray[400],
    accent: brandColors.brand[500],
  },
  bg: {
    canvas: baseTheme.colors.gray[25],
    surface: baseTheme.colors.white,
    subtle: baseTheme.colors.gray[50],
    muted: baseTheme.colors.gray[100],
    emphasis: baseTheme.colors.gray[200],
    'inverse-surface': baseTheme.colors.gray[900],
    'inverse-emphasis': baseTheme.colors.gray[600],
    'danger-subtle': baseTheme.colors.red[50],
    'danger-emphasis': baseTheme.colors.red[100],
    'success-subtle': baseTheme.colors.green[50],
    'success-emphasis': baseTheme.colors.green[100],
    'warning-emphasis': baseTheme.colors.orange[100],
    'warning-subtle': baseTheme.colors.orange[50],
    'info-subtle': baseTheme.colors.blue[50],
    'info-emphasis': baseTheme.colors.blue[100],
    accent: {
      canvas: brandColors.brand[50],
      subtle: brandColors.brand[100],
      muted: brandColors.brand[200],
      emphasis: brandColors.brand[300],
      'inverse-default': brandColors.brand[500],
      'inverse-subtle': brandColors.brand[600],
      'inverse-muted': brandColors.brand[400],
      'inverse-emphasis': brandColors.brand[700],
    },
  },
  fg: {
    default: baseTheme.colors.gray[900],
    muted: baseTheme.colors.gray[600],
    emphasis: baseTheme.colors.gray[700],
    disabled: baseTheme.colors.gray[400],
    subtle: baseTheme.colors.gray[500],
    inverse: baseTheme.colors.white,
    'inverse-muted': baseTheme.colors.gray[300],
    'inverse-subtle': baseTheme.colors.gray[200],
    'on-disabled': baseTheme.colors.gray[600],
    accent: {
      default: brandColors.brand[500],
      muted: brandColors.brand[300],
      subtle: brandColors.brand[400],
      emphasis: brandColors.brand[600],
      inverse: baseTheme.colors.white,
    },
  },
  alpha: {
    'dark-4': baseTheme.colors.blackAlpha[50],
    'dark-6': baseTheme.colors.blackAlpha[100],
    'dark-8': baseTheme.colors.blackAlpha[200],
    'dark-16': baseTheme.colors.blackAlpha[300],
    'light-4': baseTheme.colors.whiteAlpha[50],
    'light-6': baseTheme.colors.whiteAlpha[100],
    'light-8': baseTheme.colors.whiteAlpha[200],
    'light-16': baseTheme.colors.whiteAlpha[300],
  },
};

export const chakraTheme = extendTheme(
  {
    components: {
      Menu,
      FormLabel: {
        baseStyle: {
          color: 'gray.500',
          marginTop: '0.5rem',
          marginBottom: '2px',
        },
      },
      Modal,
      Button,
      Badge: {
        baseStyle: {
          fontWeight: 'medium',
        },
        variants: {
          muted: ({ colorScheme }: StyleFunctionProps) => ({
            bg: 'bg.muted',
            color: `${colorScheme}.600`,
          }),
        },
      },
      Avatar: {
        sizes: {
          lg: {
            width: '54px',
            height: '54px',
          },
        },
      },
      Heading: {
        sizes: {
          tiny: {
            fontSize: 'lg',
            fontWeight: 'semibold',
          },
        },
      },
      Table: tableTheme,
      Dashboard: {
        sizes: {
          mainContentTopOffset: '8.5rem',
          sidePanelTopOffset: '11rem',
        },
      },
    },
    styles: {
      global: () => {
        const globalStyles = {
          '.content-bouble .content': {
            cursor: 'text',
          },
        };

        return globalStyles;
      },
    },
    sizes: {
      'sidebar-expanded': '11rem',
      'sidebar-collapsed': '3.5rem',
      'sub-sidebar': '15.25rem',
      'sub-sidebar-sm': '11.25rem',
      'side-panel': '22.5rem',
      'search-input': '14rem',
      navbar: '4.5rem',
    },
    colors: { ...brandColors, ...dukeLightColors },
    fonts: {
      heading: "'Inter Variable', -apple-system, system-ui, sans-serif",
      body: "'Inter Variable', -apple-system, system-ui, sans-serif",
    },
  },
  proTheme,
);
